import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { emptyOrder, Order } from '@components/framework/orders/common.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { EditLoadingLocationOrderRequestInDTO, FeaturesEnum } from '@api/ringil-api.v2.tsx';
import {
  createOrdersComponentValidationSchema,
  fullRequiredConfig,
} from '@components/framework/orders/validation.ts';

interface ComponentProps {
  applicationId: string;
  expeditionId: string;
  localityName: string;
  locationId: string;
  onContinue: () => void;
  orders: Order[];
  allUnloadingLocations: { id: string; name: string }[];
}

interface EditExpeditionOrdersForm {
  orders: Order[];
}

export const useCreateEditLoadingOrderSidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditLoadingOrderSidebar: React.FC<ComponentProps> = ({ onContinue, applicationId, locationId, expeditionId, orders, allUnloadingLocations }) => {
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { editLoadingLocationOrderExpedition } = useContext(ApiContext);
    const { BackendValidationComponent } = useBEValidation();
    const [loading, setLoading] = useState(false);

    const notMandatoryDimensionsFlag = useSecuredFlag('loading-orders-not-mandatory-dimensions', {
      kind: 'feature',
      description: 'Make item dimensions non-mandatory in loading orders',
      features: [FeaturesEnum.NOT_MANDATORY_DIMENSIONS],
    });
    const ordersConfig = fullRequiredConfig(!notMandatoryDimensionsFlag.granted);

    const save = (data: EditExpeditionOrdersForm) => {
      setLoading(true);
      editLoadingLocationOrderExpedition(
        expeditionId,
        locationId,
        {
          loadingLocationOrders: data.orders.map(
            t =>
              ({
                order: t,
                unloadingLocationId: t.unloadingLocationId,
              } as EditLoadingLocationOrderRequestInDTO),
          ),
        },
        () => {
          onContinue();
          setLoading(false);
          setVisible(false);
        },
      );
    };

    const schema = createOrdersComponentValidationSchema(ordersConfig, true, tr);
    const form = useForm<EditExpeditionOrdersForm>(schema, { orders: orders?.length > 0 ? orders : [emptyOrder(true, true)] }, d => save(d), false, false);

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('EditLoadingOrdersSidebar.ocekavanaManipulace', 'Očekávaná manipulace')}
          loading={loading}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <VerticalFieldAndValueWithIcon
              label={tr('EditLoadingOrdersSidebar.forExpedition', 'For expedition')}
              valueText={applicationId}
              iconPath={'/images/icons/ringil3/forExpedition.svg'}
            />
          </div>
          <>
            <OrdersComponent
              config={ordersConfig}
              allUnloadingLocations={allUnloadingLocations}
              onChange={v =>
                form.useProduce(d => {
                  d.orders = v;
                })
              }
              orders={form.values.orders}
              validationErrors={form.validationErrors}
              companyId={loggedUser.companyId}
            />
          </>
        </RingilSidebar>
      </>
    );
  };

  return { EditLoadingOrderSidebar, setVisible, visible };
};
